import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TRANSACTION_PAGE_TABS } from 'config';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import * as transactionsSelectors from 'store/transactionsSlice/transactionsSelectors';
import PrimaryTextButton from 'components/UI/Buttons/Primary/PrimaryTextButton';
import CountdownIconButton from 'components/UI/Buttons/CountdownIconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import PropTypes from 'prop-types';
import styles from './RealTimeUpdateVideoWall.module.css';

const RealTimeUpdateVideoWall = ({ show = false }) => {
    const dispatch = useDispatch();
    const [isVisible, setIsVisible] = useState(false);
    const [videoWallState, setVideoWallState] = useState({
        showLoading: false,
        isAnimating: false,
        showContent: false,
    });
    const animationTimeoutRef = useRef(null);
    const loadingTimeoutRef = useRef(null);
    const isVideoWallEnabled = useSelector(transactionsSelectors.selectIsVideoWallEnabled);
    const activeTab = useSelector(transactionsSelectors.selectActiveTab);
    const isLoadingRealTimeStreams = useSelector(transactionsSelectors.selectIsLoadingRealTimeStreams);
    const showNewCameras = useSelector(transactionsSelectors.selectIsShowNewCameras);
    const isVideoWallTabActive = activeTab === TRANSACTION_PAGE_TABS.VIDEO_WALL_TAB.ID;
    const showNewCamerasButton = isVideoWallEnabled && isVideoWallTabActive && showNewCameras;

    useEffect(() => {
        if (show) {
            setIsVisible(true);
            setVideoWallState({ showLoading: false, isAnimating: false, showContent: false });

            if (isLoadingRealTimeStreams) {
                setVideoWallState((prev) => ({ ...prev, showLoading: true }));
                loadingTimeoutRef.current = setTimeout(() => {
                    setVideoWallState({ showLoading: false, isAnimating: true, showContent: true });
                }, 500);
            } else {
                animationTimeoutRef.current = setTimeout(() => {
                    setVideoWallState({ showLoading: false, isAnimating: true, showContent: true });
                }, 50);
            }
        } else if (isVisible) {
            setVideoWallState((prev) => ({ ...prev, isAnimating: false }));
            animationTimeoutRef.current = setTimeout(() => {
                setIsVisible(false);
                setVideoWallState({ showLoading: false, isAnimating: false, showContent: false });
            }, 300);
        }

        return () => {
            if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        };
    }, [show, isVisible, isLoadingRealTimeStreams]);

    const onClose = () => {
        setVideoWallState((prev) => ({ ...prev, isAnimating: false }));
        animationTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
            setVideoWallState({ showLoading: false, isAnimating: false, showContent: false });
            dispatch(transactionActions.showRealTimeUpdateMessages({ isLoadingRealTimeStreams: false, showRealTimeUpdateMessages: false }));
        }, 300);
    };

    const animationClass = `${styles.animated} ${videoWallState.isAnimating ? styles['slide-in'] : styles['slide-out']}
    }`;

    if (!isVisible) return null;

    const renderLoadingContent = () => (
        <>
            <span className={styles['text']}>Video Wall is being updated. </span>
            <CircularProgress size="1.5rem" />
        </>
    );

    const renderUpdatedContent = () => (
        <>
            <span className={styles['text']}>Video wall has been updated.</span>
            {showNewCamerasButton && (
                <PrimaryTextButton
                    text="Show new cameras"
                    size="small"
                    onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                />
            )}
            <CountdownIconButton icon={<CloseIcon sx={{ color: '#a4a0ad', width: '1.25rem', height: '1.25rem' }} />} onClick={onClose} />
        </>
    );

    return (
        <div className={`${styles['real-time-update-notification--wrapper']} ${animationClass}`}>
            {videoWallState.showLoading ? renderLoadingContent() : videoWallState.showContent ? renderUpdatedContent() : null}
        </div>
    );
};

RealTimeUpdateVideoWall.propTypes = {
    show: PropTypes.bool,
};

export default RealTimeUpdateVideoWall;
